import { useContext, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { scheduleCrud } from "@/api/schedule";
import type { SchedulePauseReason } from "@/api/schedule/models";
import { Button } from "@/components/common/Button";
import { InputDropdown } from "@/components/common/inputs";
import { ModalAlert } from "@/components/common/modals";
import { SCHEDULE_PAUSE_REASON_OPTIONS } from "@/constants/schedule";
import { useDepartmentRoute } from "@/hooks/common/use-department-route";
import { useForm } from "@/hooks/common/use-form";
import { showToast } from "@/providers";
import { useUserStore } from "@/stores/use-user-store";
import { canManageSchedulePause } from "@/utils/features/schedule/permission";
import { getUserRoleByDepartment, isProducerUnavailableInDepartment } from "@/utils/user-positions";

import { Icon } from "@/components/common/Icon";
import { ScheduleActionsContext } from "../Provider";

export const ScheduleActionsPause = () => {
  const [isOpen, setIsOpen] = useState(false);
  const activeUser = useUserStore((state) => state.activeUser);
  const queryClient = useQueryClient();
  const { department } = useDepartmentRoute();
  const activeRole = getUserRoleByDepartment(activeUser, department);
  const { schedule } = useContext(ScheduleActionsContext);
  const { action, control, isSubmitting } = useForm({
    initialValues: { reason: undefined as SchedulePauseReason | undefined },
    handleSubmit: async ({ reason }) => {
      await scheduleCrud.pause(schedule.id, { reason: reason! });
      return null;
    },
    fields: { reason: { validation: "required" } },
    options: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["schedule"], refetchType: "all" });
        setIsOpen(false);
        showToast({ type: "success", title: "Atendimento pausado!" });
      },
      onError: () =>
        showToast({
          type: "error",
          title: "Erro ao pausar atendimento.",
          content: "Tente novamente em alguns instantes.",
        }),
    },
  });

  if (
    isProducerUnavailableInDepartment(activeUser, department) ||
    !canManageSchedulePause(activeRole, activeUser?.id, schedule, "IN_PROGRESS")
  ) {
    return null;
  }

  return (
    <>
      <Button
        leading={<Icon name="Pause" className="text-error" />}
        variant="destructive"
        onClick={() => setIsOpen(true)}
        className="[&]:bg-transparent"
        full
      >
        Pausar atendimento
      </Button>

      <ModalAlert
        isOpen={isOpen}
        onClose={isSubmitting ? undefined : () => setIsOpen(false)}
        isOverlayCloseable={!isSubmitting}
        cancelable
        title="Pausar atendimento?"
        description="Selecione o motivo para pausar o atendimento."
        actions={[
          {
            type: "submit",
            form: "pause-schedule-form",
            children: "Pausar atendimento",
            variant: "destructive",
            isLoading: isSubmitting,
          },
        ]}
      >
        <form className="form-flow" onSubmit={action} id="pause-schedule-form">
          <InputDropdown
            field="reason"
            label="Motivo da pausa"
            options={SCHEDULE_PAUSE_REASON_OPTIONS}
            control={control}
          />
        </form>
      </ModalAlert>
    </>
  );
};
